export const WAITLIST_PRESET_ANY = 'any';
export const WAITLIST_NAMED_WINDOWS = ['morning', 'afternoon', 'evening'];
export const WAITLIST_PRESETS = [WAITLIST_PRESET_ANY, ...WAITLIST_NAMED_WINDOWS];

const toDateKey = (value) => String(value || '').slice(0, 10);

export const dateHasSpecialOpening = (dateStr, specialOpeningDays, teamMemberIds) => {
  const dateKey = toDateKey(dateStr);
  const matching = (specialOpeningDays || []).filter((item) => toDateKey(item.date || item.Date) === dateKey);
  if (matching.length === 0) {
    return false;
  }
  if (!teamMemberIds || teamMemberIds.length === 0) {
    return true;
  }
  const ids = new Set(teamMemberIds.map(String));
  return matching.some((item) => ids.has(String(item.teamMemberId || item.TeamMemberId)));
};

const memberHoursId = (entry) => String(entry.teamMemberId || entry.TeamMemberId);
const memberHoursDays = (entry) => entry.workingHours || entry.WorkingHours;

export const isStoreClosedOnDate = (
  dateStr,
  openingHours,
  authorizationHours,
  specialOpeningDays,
  teamMemberIds,
  teamMemberWorkingHours,
) => {
  const memberHours = teamMemberWorkingHours || [];
  if (
    (!openingHours || !openingHours.length)
    && (!authorizationHours || !authorizationHours.length)
    && !(specialOpeningDays || []).length
    && memberHours.length === 0
  ) {
    return false;
  }
  const d = new Date(`${dateStr}T12:00:00`);
  const weekDay = d.toLocaleDateString('en-US', { weekday: 'long' });
  const hasPeriods = (days) => {
    const dayHours = (days || []).find((item) => item.weekDay === weekDay);
    return Boolean(dayHours?.periods?.length);
  };
  const storeOpen = hasPeriods(openingHours) || hasPeriods(authorizationHours);
  const selectedIds = (teamMemberIds || []).map(String);

  if (selectedIds.length === 0) {
    return !storeOpen
      && !dateHasSpecialOpening(dateStr, specialOpeningDays, [])
      && !memberHours.some((entry) => hasPeriods(memberHoursDays(entry)));
  }

  const customHoursByMember = new Map(memberHours.map((entry) => [memberHoursId(entry), memberHoursDays(entry)]));
  return !selectedIds.some((id) => {
    const customHours = customHoursByMember.get(id);
    const works = customHours ? hasPeriods(customHours) : storeOpen;
    return works || dateHasSpecialOpening(dateStr, specialOpeningDays, [id]);
  });
};

export const normalizeWaitlistTime = (value) => {
  if (!value) return '';
  return String(value).slice(0, 5);
};

export const getWaitlistWindowKey = (window) => String(window?.key || window?.Key || '');

export const findWaitlistWindow = (windows, key) =>
  (windows || []).find((item) => getWaitlistWindowKey(item) === key);

export const getOpeningSpan = (windows) => {
  const ranges = (windows || [])
    .map((item) => ({
      from: normalizeWaitlistTime(item.from || item.From),
      to: normalizeWaitlistTime(item.to || item.To),
    }))
    .filter((item) => item.from && item.to);
  if (ranges.length === 0) return null;
  return {
    from: ranges.reduce((min, item) => (item.from < min ? item.from : min), ranges[0].from),
    to: ranges.reduce((max, item) => (item.to > max ? item.to : max), ranges[0].to),
  };
};

export const matchWaitlistPreset = (from, to, windows) => {
  const normalizedFrom = normalizeWaitlistTime(from);
  const normalizedTo = normalizeWaitlistTime(to);
  if (!normalizedFrom && !normalizedTo) return WAITLIST_PRESET_ANY;
  return WAITLIST_NAMED_WINDOWS.find((key) => {
    const window = findWaitlistWindow(windows, key);
    if (!window) return false;
    return normalizeWaitlistTime(window.from || window.From) === normalizedFrom
      && normalizeWaitlistTime(window.to || window.To) === normalizedTo;
  }) || '';
};

export const applyWaitlistPreset = (preset, windows) => {
  if (preset === WAITLIST_PRESET_ANY) {
    return { preset: WAITLIST_PRESET_ANY, from: '', to: '' };
  }
  const window = findWaitlistWindow(windows, preset);
  if (!window) return { preset: WAITLIST_PRESET_ANY, from: '', to: '' };
  return {
    preset,
    from: normalizeWaitlistTime(window.from || window.From),
    to: normalizeWaitlistTime(window.to || window.To),
  };
};

const parseMinutes = (value) => {
  const [hours, minutes] = normalizeWaitlistTime(value).split(':').map(Number);
  if (!Number.isFinite(hours) || !Number.isFinite(minutes)) return null;
  return (hours * 60) + minutes;
};

const formatMinutes = (total) => {
  const hours = Math.floor(total / 60);
  const minutes = total % 60;
  return `${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}`;
};

export const buildWaitlistClockOptions = (windows, stepMinutes = 15) => {
  const span = getOpeningSpan(windows);
  const times = new Set();
  (windows || []).forEach((item) => {
    const from = normalizeWaitlistTime(item.from || item.From);
    const to = normalizeWaitlistTime(item.to || item.To);
    if (from) times.add(from);
    if (to) times.add(to);
  });
  if (span) {
    const start = parseMinutes(span.from);
    const end = parseMinutes(span.to);
    if (start != null && end != null) {
      for (let cursor = start; cursor <= end; cursor += stepMinutes) {
        times.add(formatMinutes(cursor));
      }
    }
  }
  return [...times].sort();
};

export const formatWaitlistWindowLabel = (window, t) => {
  if (!window) return '';
  const key = getWaitlistWindowKey(window);
  if (WAITLIST_NAMED_WINDOWS.includes(key)) {
    return t(`customer.appointments.booking.${key}`);
  }
  const from = window.from || window.From;
  const to = window.to || window.To;
  if (from && to) {
    return `${normalizeWaitlistTime(from)} – ${normalizeWaitlistTime(to)}`;
  }
  return key.replace(/^range:/, '');
};

export const toDateInputValue = (value) => {
  if (!value) return '';
  if (value instanceof Date && !Number.isNaN(value.getTime())) {
    const year = value.getFullYear();
    const month = String(value.getMonth() + 1).padStart(2, '0');
    const day = String(value.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  }
  return String(value).slice(0, 10);
};
